/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react"
import VideocamIcon from "@material-ui/icons/Videocam"
import DeleteIcon from "@material-ui/icons/Delete"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import "./GalleryUploader.css"

const placeHolder = require("assets/img/placeholder.jpg")

/**
 * Composant de galerie - La gestion de l'upload est déléguée au parent
 * @param {Object} props
 * @param {Array} props.images - Images à afficher [{id, url, type, isNew, uploading}]
 * @param {Function} props.onFilesSelected - Callback quand des fichiers sont sélectionnés (files)
 * @param {Function} props.onRemoveImage - Callback quand une image doit être supprimée (image, index)
 * @param {string} props.title - Titre du composant
 * @param {number} props.maxMedias - Nombre maximum de médias
 * @param {boolean} props.disabled - Désactive l'upload
 * @param {number} props.progress - Progression de l'upload (0-100)
 * @param {string} props.progressMessage - Message de progression
 *
 * Source : https://codesandbox.io/p/sandbox/single-and-multiple-image-upload-in-react-2ojpdd?file=%2Fcomponents%2FIterateUpload.js
 */
const GalleryUploader = ({
  images = [],
  onFilesSelected,
  onRemoveImage,
  title = "Ajouter des médias à la galerie",
  maxMedias = 10,
  disabled = false,
  progress = 0,
  progressMessage = "",
}) => {
  /**
   * Détecte le type de média à partir du fichier
   */
  const getMediaType = (file) => {
    if (file.type.startsWith("video/")) return "video"
    if (file.type.startsWith("image/")) return "image"
    return "image"
  }

  /**
   * Gère la sélection de fichiers
   */
  const handleFileSelect = (e) => {
    e.preventDefault()
    const files = Array.from(e.target.files)

    if (!files.length) return

    if (images.length + files.length > maxMedias) {
      alert(`Vous ne pouvez ajouter que ${maxMedias} médias maximum`)
      return
    }

    // Préparer les fichiers avec des métadonnées
    const filesWithMeta = files.map((file) => ({
      file,
      type: getMediaType(file),
      preview: URL.createObjectURL(file),
    }))

    // Appeler le callback du parent
    if (onFilesSelected) {
      onFilesSelected(filesWithMeta)
    }

    // Réinitialiser l'input
    e.target.value = null
  }

  /**
   * Gère la suppression d'une image
   */
  const handleRemove = (image, index) => {
    if (onRemoveImage) {
      onRemoveImage(image, index)
    }
  }

  /**
   * Nettoyage des URLs blob au démontage
   */
  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.preview && img.preview.startsWith("blob:")) {
          URL.revokeObjectURL(img.preview)
        }
      })
    }
  }, [images])

  return (
    <div className="gallery-uploader">
      <legend>{title}</legend>

      {progress > 0 && (
        <div className="upload-progress">
          <div className="progress-bar" style={{ width: `${progress}%` }}>
            {progress}%
          </div>
          <p className="progress-message">{progressMessage}</p>
        </div>
      )}

      <div className="gallery-grid">
        {images.map((media, index) => (
          <div key={media.id || media.preview || index} className="media-item">
            <div className="media-wrapper">
              {media.type === "video" ? (
                <>
                  <video
                    src={media.url || media.preview}
                    className="media-preview"
                    muted
                    controls
                  />
                  <VideocamIcon className="video-icon" />
                </>
              ) : (
                <img
                  src={media.url || media.preview}
                  alt={`Media ${index + 1}`}
                  className="media-preview"
                />
              )}

              {media.uploading && (
                <div className="uploading-overlay">
                  <CloudUploadIcon />
                  <span>Upload...</span>
                </div>
              )}

              <button
                className="remove-btn"
                onClick={() => handleRemove(media, index)}
                disabled={disabled}
                type="button"
              >
                <DeleteIcon />
              </button>
            </div>
          </div>
        ))}

        {images.length < maxMedias && !disabled && (
          <div className="media-item">
            <label className="upload-placeholder">
              <img
                src={placeHolder.default}
                alt="Upload"
                className="placeholder-img"
              />
              <div className="upload-overlay">
                <span className="plus-icon">+</span>
              </div>
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelect}
                className="file-input"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  )
}

export default GalleryUploader
